/**
 * AirQualityChartBase - shared logic for Style01/02
 * Range dropdown + air quality sensor switch (pm25, pm10, aqi, co2)
 * Style01: AreaChart, Style02: BarChart
 * When deviceSN + credentials provided: fetches real data from GenerateReport API
 * When no deviceSN: falls back to mock data
 */
import { useState, useMemo, useEffect } from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  getAirQualitySensors,
  MOCK_AIR_QUALITY_SENSOR_OPTIONS,
  type AirQualitySensorId,
} from './airQualitySensorConfig'
import { CHARTABLE_SENSOR_CONFIG } from './chartableSensorConfig'
import type { ChartableSensorId, ChartableSensorOption } from './chartableSensorConfig'
import {
  querySensorReportWithAuthContext,
  type GenerateReportDeviceData,
} from '../../services/turingApi'
import './AirQualityChart.css'

export type TimeRange = '1H' | '24H' | '7D' | '30D'

const TIME_RANGES: { id: TimeRange; label: string }[] = [
  { id: '1H', label: '1H' },
  { id: '24H', label: '24H' },
  { id: '7D', label: '7D' },
  { id: '30D', label: '30D' },
]

/* ---- Time range -> GenerateReport params ---- */

function mapTimeRangeToGenerateReport(range: TimeRange): {
  duration: string;
  granularity: 'hour' | 'day';
} {
  if (range === '1H') return { duration: '1h', granularity: 'hour' }
  if (range === '24H') return { duration: '24h', granularity: 'hour' }
  if (range === '7D') return { duration: '7d', granularity: 'day' }
  return { duration: '30d', granularity: 'day' }
}

/* ---- Value extraction helpers (shared with TemperatureChartBase) ---- */

function toTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : null
}

function toNumber(value: unknown): number | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    return toNumber(value[0])
  }
  if (value && typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if ('value' in rec) return toNumber(rec.value)
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const num = Number(value)
    return Number.isFinite(num) ? num : null
  }
  return null
}

function resolveSensorValue(point: Record<string, unknown>, sensor: ChartableSensorId): number | null {
  const preferredKeys: Record<string, string[]> = {
    pm25: ['pm25', 'pm2_5', 'avgPm25'],
    pm10: ['pm10', 'avgPm10'],
    aqi: ['aqi', 'avgAqi'],
    co2: ['co2', 'avgCo2'],
  }
  for (const key of preferredKeys[sensor] || [sensor]) {
    const num = toNumber(point[key])
    if (num !== null) return num
  }
  return null
}

function resolveReportDeviceData(
  reportData: Record<string, GenerateReportDeviceData> | undefined,
  deviceSN: string
): GenerateReportDeviceData | null {
  if (!reportData) return null
  if (reportData[deviceSN]) return reportData[deviceSN]
  const baseSN = String(deviceSN || '').split('#')[0]
  if (reportData[baseSN]) return reportData[baseSN]
  for (const device of Object.values(reportData)) {
    if (!device) continue
    if (device.deviceSN === deviceSN) return device
    if (device.baseSN === baseSN) return device
  }
  return null
}

/* ---- Mock data generator (fallback when no deviceSN) ---- */

function generateMockData(
  range: TimeRange,
  sensorId: AirQualitySensorId
): Array<{ time: number; value: number }> {
  const cfg = CHARTABLE_SENSOR_CONFIG[sensorId]
  if (!cfg) return []

  const now = Date.now()
  const points = range === '1H' ? 12 : range === '24H' ? 24 : range === '7D' ? 7 : 30
  const intervalMs =
    range === '1H'
      ? 5 * 60 * 1000
      : range === '24H'
        ? 60 * 60 * 1000
        : 24 * 60 * 60 * 1000

  const data: Array<{ time: number; value: number }> = []
  let base = cfg.base
  for (let i = points; i >= 0; i--) {
    const t = new Date(now - i * intervalMs)
    base += (Math.random() - 0.5) * 2
    base = Math.max(cfg.min, Math.min(cfg.max, base))
    const value = Math.round(base * 10) / 10
    data.push({ time: t.getTime(), value })
  }
  return data
}

function formatTimeLabel(timestamp: number, range: TimeRange): string {
  const t = new Date(timestamp)
  if (range === '7D' || range === '30D') {
    return t.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
  }
  return t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

export type AirQualityChartVariant = 'blue' | 'gold'
export type AirQualityChartType = 'area' | 'bar'

export interface AirQualityChartBaseProps {
  variant: AirQualityChartVariant
  chartType: AirQualityChartType
  deviceSN?: string | null
  credentials?: { apiKey?: string; accessToken?: string; appId?: string }
}

const VARIANT_STYLES: Record<
  AirQualityChartVariant,
  {
    stroke: string
    fill: string
    gradientId: string
    gradientFrom: string
    gradientTo: string
    dropdownActiveClass: string
  }
> = {
  blue: {
    stroke: '#38bdf8',
    fill: '#38bdf8',
    gradientId: 'airQualityGradient-blue',
    gradientFrom: '#38bdf8',
    gradientTo: '#38bdf8',
    dropdownActiveClass: 'air-quality-chart__dropdown--blue',
  },
  gold: {
    stroke: '#fbbf24',
    fill: '#fbbf24',
    gradientId: 'airQualityGradient-gold',
    gradientFrom: '#fbbf24',
    gradientTo: '#fbbf24',
    dropdownActiveClass: 'air-quality-chart__dropdown--gold',
  },
}

export function AirQualityChartBase({
  variant,
  chartType,
  deviceSN,
  credentials,
}: AirQualityChartBaseProps) {
  const hasDeviceSN = Boolean(deviceSN?.trim())
  const hasAuthCred = Boolean(credentials?.apiKey || credentials?.accessToken)
  const shouldQuerySensors = Boolean(hasDeviceSN && hasAuthCred)

  const [range, setRange] = useState<TimeRange>('24H')
  const [sensorOptions, setSensorOptions] = useState<ChartableSensorOption[]>(
    shouldQuerySensors ? [] : MOCK_AIR_QUALITY_SENSOR_OPTIONS
  )
  const [sensor, setSensor] = useState<AirQualitySensorId>('pm25')
  const [loading, setLoading] = useState(shouldQuerySensors)
  const [dataLoading, setDataLoading] = useState(shouldQuerySensors)
  const [data, setData] = useState<Array<{ time: number; value: number }>>(() =>
    generateMockData('24H', 'pm25')
  )

  /* ---- Fetch available sensors ---- */
  useEffect(() => {
    let cancelled = false
    if (!hasDeviceSN) {
      setSensorOptions(MOCK_AIR_QUALITY_SENSOR_OPTIONS)
      setLoading(false)
      return () => { cancelled = true }
    }
    if (!hasAuthCred) {
      setSensorOptions([])
      setLoading(false)
      return () => { cancelled = true }
    }

    setLoading(true)
    getAirQualitySensors(deviceSN, credentials)
      .then((opts) => {
        if (!cancelled) {
          setSensorOptions(opts)
          setSensor((prev) => {
            const has = opts.some((o) => o.id === prev)
            return has ? prev : ((opts[0]?.id as AirQualitySensorId) ?? 'pm25')
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [deviceSN, credentials?.apiKey, credentials?.accessToken, credentials?.appId, hasDeviceSN, hasAuthCred])

  /* ---- Fetch chart data (real API or mock) ---- */
  useEffect(() => {
    let cancelled = false

    if (!hasDeviceSN) {
      setData(generateMockData(range, sensor))
      setDataLoading(false)
      return () => { cancelled = true }
    }
    if (!hasAuthCred) {
      setData([])
      setDataLoading(false)
      return () => { cancelled = true }
    }

    setDataLoading(true)
    const reportScope = mapTimeRangeToGenerateReport(range)
    querySensorReportWithAuthContext(
      {
        apiKey: credentials?.apiKey,
        accessToken: credentials?.accessToken,
        appId: credentials?.appId,
      },
      deviceSN!.trim(),
      {
        duration: reportScope.duration,
        granularity: reportScope.granularity,
        metrics: 'auto',
        format: 'summary',
      }
    )
      .then((reportResponse) => {
        if (cancelled) return
        const deviceData = resolveReportDeviceData(reportResponse.data, deviceSN!.trim())
        const rows = Array.isArray(deviceData?.chartData) ? deviceData.chartData : []
        const next = rows
          .map((row) => {
            const point = row as Record<string, unknown>
            const time = toTimestamp(point.time)
            const value = resolveSensorValue(point, sensor)
            if (time === null || value === null) return null
            return { time, value }
          })
          .filter((point): point is { time: number; value: number } => point !== null)
          .sort((a, b) => a.time - b.time)
        setData(next)
      })
      .catch(() => {
        if (cancelled) return
        setData([])
      })
      .finally(() => {
        if (cancelled) return
        setDataLoading(false)
      })

    return () => { cancelled = true }
  }, [deviceSN, range, sensor, credentials?.apiKey, credentials?.accessToken, credentials?.appId, hasDeviceSN, hasAuthCred])

  const styles = VARIANT_STYLES[variant]
  const cfg = CHARTABLE_SENSOR_CONFIG[sensor]

  const stats = useMemo(() => {
    if (data.length === 0) return { current: null, max: null, min: null }
    const vals = data.map((d) => d.value)
    const current = vals[vals.length - 1] ?? 0
    const max = Math.max(...vals)
    const min = Math.min(...vals)
    return { current, max, min }
  }, [data])

  const formatStatValue = (value: number | null): string => {
    if (value == null) return '--'
    return `${value}`
  }
  const formatY = (v: number) => `${v}`
  const formatTooltip = (value: number | string): [string, string] => {
    const numeric = typeof value === 'number' ? value : Number(value || 0)
    return cfg ? [`${numeric}${cfg.unit}`, cfg.label] : ['', '']
  }

  if (loading || dataLoading) {
    return (
      <div className={`air-quality-chart air-quality-chart--${variant}`}>
        <div className="air-quality-chart__loading">Loading sensors...</div>
      </div>
    )
  }

  if (sensorOptions.length === 0) {
    return (
      <div className={`air-quality-chart air-quality-chart--${variant}`}>
        <div className="air-quality-chart__loading">No sensor data</div>
      </div>
    )
  }

  const chartMargin = { top: 8, right: 8, left: 8, bottom: 8 }
  const axisStyle = {
    tick: { fill: 'rgba(148,163,184,0.8)', fontSize: 10 },
    axisLine: { stroke: 'rgba(255,255,255,0.2)' },
    tickLine: { stroke: 'rgba(255,255,255,0.2)' },
  }
  const tooltipStyle = {
    contentStyle: {
      background: 'rgba(15,23,42,0.95)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 8,
    },
    labelStyle: { color: '#94a3b8' },
  }

  return (
    <div className={`air-quality-chart air-quality-chart--${variant}`}>
      <div className="air-quality-chart__controls">
        <div className={`air-quality-chart__dropdown ${styles.dropdownActiveClass}`}>
          <span className="air-quality-chart__dropdown-icon material-symbols-rounded" aria-hidden>schedule</span>
          <select
            id={`aqc-range-${variant}`}
            value={range}
            onChange={(e) => setRange(e.target.value as TimeRange)}
            className="air-quality-chart__select"
            aria-label="Time range"
          >
            {TIME_RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className={`air-quality-chart__dropdown ${styles.dropdownActiveClass}`}>
          <span className="air-quality-chart__dropdown-icon material-symbols-rounded" aria-hidden>air</span>
          <select
            id={`aqc-sensor-${variant}`}
            value={sensor}
            onChange={(e) => setSensor(e.target.value as AirQualitySensorId)}
            className="air-quality-chart__select"
            aria-label="Air quality sensor type"
          >
            {sensorOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div
        className="air-quality-chart__chart"
        data-css-slot="chartLine"
        data-css-slot-label="Chart Line"
      >
        {data.length === 0 ? (
          <div className="air-quality-chart__loading">No sensor data</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            {chartType === 'area' ? (
              <AreaChart data={data} margin={chartMargin}>
                <defs>
                  <linearGradient id={styles.gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={styles.gradientFrom} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={styles.gradientTo} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  {...axisStyle}
                  tickFormatter={(value) => formatTimeLabel(Number(value), range)}
                />
                <YAxis domain={['dataMin - 2', 'dataMax + 2']} {...axisStyle} tickFormatter={formatY} />
                <Tooltip
                  {...tooltipStyle}
                  labelFormatter={(label) => formatTimeLabel(Number(label), range)}
                  formatter={(value) => formatTooltip(value)}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={styles.stroke}
                  strokeWidth={2}
                  fill={`url(#${styles.gradientId})`}
                />
              </AreaChart>
            ) : (
              <BarChart data={data} margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  {...axisStyle}
                  tickFormatter={(value) => formatTimeLabel(Number(value), range)}
                />
                <YAxis domain={['dataMin - 2', 'dataMax + 2']} {...axisStyle} tickFormatter={formatY} />
                <Tooltip
                  {...tooltipStyle}
                  cursor={{ fill: 'transparent', stroke: styles.stroke, strokeWidth: 2 }}
                  labelFormatter={(label) => formatTimeLabel(Number(label), range)}
                  formatter={(value) => formatTooltip(value)}
                />
                <Bar dataKey="value" fill={styles.fill} radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
      <div className="air-quality-chart__stats">
        <span>Now {formatStatValue(stats.current)}{stats.current == null ? '' : cfg.unit}</span>
        <span>Max {formatStatValue(stats.max)}{stats.max == null ? '' : cfg.unit}</span>
        <span>Min {formatStatValue(stats.min)}{stats.min == null ? '' : cfg.unit}</span>
      </div>
    </div>
  )
}
